import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Table,
  CircularProgress,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Switch,
  FormControlLabel,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Delete, Edit, Add, FilterAltOff, Close, AddPhotoAlternate } from '@mui/icons-material';
import { citiesAPI, languageSupportAPI } from '../../services/api';
import { clearFilterButtonSx } from '../../utils/clearFilterButtonSx';
import { useLanguage } from '../../context/LanguageContext';
import { getAdminUser } from '../../utils/authSessionStorage';
import browserGlobal from '../../utils/browserGlobal';

const defaultTranslation = () => ({ languageCode: 'en', name: '' });

const defaultCityImage = 'https://placehold.co/400x300/7c3aed/ffffff?text=No+Image';

const validateUploadedFile = (file) => {
  if (!file) return null;
  const name = file.name || '';
  const lowercaseName = name.toLowerCase();

  // Allowed extensions
  const allowedExtensions = ['.png', '.jpg', '.jpeg', '.svg'];
  const hasAllowedExt = allowedExtensions.some(ext => lowercaseName.endsWith(ext));
  if (!hasAllowedExt) {
    return 'Only .png, .jpg, .jpeg, and .svg files are allowed.';
  }

  // Block executables/installers explicitly
  const blockedExtensions = ['.exe', '.msi', '.bat', '.sh', '.cmd', '.com', '.scr', '.vbs', '.js'];
  const hasBlockedExt = blockedExtensions.some(ext => lowercaseName.endsWith(ext));
  if (hasBlockedExt) {
    return 'Installer and executable files (.exe, .msi, .bat, etc.) are blocked.';
  }

  // Blocks multi-extensions (abc.xyz.png)
  const dots = name.split('.').length - 1;
  if (dots > 1) {
    return 'Files with multiple extensions (e.g. abc.xyz.png) are not allowed.';
  }

  // Max size: 5 MB
  const maxSizeBytes = 5 * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return 'File size must be under 5 MB.';
  }

  return null;
};

const CityList = () => {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));
  const adminUser = getAdminUser();
  const isSuperAdmin = /super\s*admin/i.test(adminUser.role || '');

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cities, setCities] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toast, setToast] = useState({ text: '', type: 'info' });
  const handleClearFilters = () => {
    setFilters({
      name: '',
      cityCode: '',
      status: ''
    });
  };

  const [filters, setFilters] = useState({
    name: '',
    cityCode: '',
    status: ''
  });

  const [formData, setFormData] = useState({
    name: '',
    cityCode: '',
    Pictures: [],
    existingBannerImages: [],
    picturesRemove: [],
    isEnabled: true,
    translations: [defaultTranslation()],
  });
  const [languages, setLanguages] = useState([]);

  const showToast = (text, type = 'info') => {
    setToast({ text, type });
    setToastOpen(true);
  };

  const fetchLanguages = async () => {
    try {
      const response = await languageSupportAPI.getLanguages(0, 500);
      if (response?.data?.success === true && Array.isArray(response.data.payload)) {
        const list = (response.data.payload || []).filter((l) => l.isActive !== false);
        setLanguages(list);
      } else {
        setLanguages([]);
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
      setLanguages([]);
    }
  };

  const fetchCities = async () => {
    setIsLoading(true);
    try {
      const response = await citiesAPI.getCitiess();
      if (response?.data?.success === true && Array.isArray(response.data.payload)) {
        setCities(response.data.payload);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    fetchLanguages();
  }, []);

  const handleOpen = async (city = null) => {
    if (city) {
      setIsLoading(true);
      try {
        const response = await citiesAPI.getCity(city.id);
        const cityData = response?.data?.payload || city;

        let existingImages = [];
        if (cityData.pictures && Array.isArray(cityData.pictures)) {
          existingImages = cityData.pictures.map(p => typeof p === 'string' ? p : (p.imageUrl || p.url)).filter(Boolean);
        } else if (cityData.imageUrl) {
          existingImages = cityData.imageUrl.split(',').filter(x => x.trim());
        }
        // Fallback to row data in case detail API misses pictures field
        if (existingImages.length === 0) {
          if (city?.pictures && Array.isArray(city.pictures)) {
            existingImages = city.pictures.map(p => typeof p === 'string' ? p : (p.imageUrl || p.url)).filter(Boolean);
          } else if (city?.imageUrl) {
            existingImages = city.imageUrl.split(',').filter(x => x.trim());
          }
        }

        setSelectedCity({
          ...cityData,
          id: city.id ?? cityData.id
        });
        setFormData({
          name: cityData.name || city.name,
          cityCode: cityData.cityCode || city.cityCode,
          existingBannerImages: existingImages,
          Pictures: [],
          picturesRemove: [],
          isEnabled: cityData.isEnabled ?? city.isEnabled,
          translations: (cityData.translations && cityData.translations.length)
            ? cityData.translations.map((t) => ({ languageCode: t.languageCode || '', name: t.name || '' }))
            : [defaultTranslation()],
        });
        setOpen(true);
      } catch (error) {
        console.error('Error fetching city details:', error);
        showToast(t('cities.toastFetchError') || 'Failed to fetch city details', 'error');
      } finally {
        setIsLoading(false);
      }
    } else {
      setSelectedCity(null);
      setFormData({
        name: '',
        cityCode: '',
        existingBannerImages: [],
        Pictures: [],
        picturesRemove: [],
        isEnabled: true,
        translations: [defaultTranslation()],
      });
      setOpen(true);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    let errorMsg = '';

    for (const file of files) {
      const error = validateUploadedFile(file);
      if (error) {
        errorMsg = error;
      } else {
        validFiles.push(file);
      }
    }

    if (errorMsg) {
      showToast(errorMsg, 'warning');
    }

    if (validFiles.length > 0) {
      setFormData((prev) => ({
        ...prev,
        Pictures: [...prev.Pictures, ...validFiles],
      }));
    }
    // Allow re-selecting same file after remove
    e.target.value = '';
  };

  const handleRemoveImage = (index, isExisting = false) => {
    setFormData((prev) => {
      if (isExisting) {
        const removedUrl = prev.existingBannerImages[index];
        const updatedExisting = prev.existingBannerImages.filter((_, i) => i !== index);
        return {
          ...prev,
          existingBannerImages: updatedExisting,
          picturesRemove: [
            ...(prev.picturesRemove || []),
            removedUrl
          ].filter(Boolean)
        };
      } else {
        return {
          ...prev,
          Pictures: prev.Pictures.filter((_, i) => i !== index)
        };
      }
    });
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedCity(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTranslationChange = (index, field, value) => {
    setFormData((prev) => {
      const next = [...prev.translations];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, translations: next };
    });
  };

  const addTranslation = () => {
    setFormData((prev) => ({
      ...prev,
      translations: [...prev.translations, defaultTranslation()],
    }));
  };

  const removeTranslation = (index) => {
    if (formData.translations.length <= 1) return;
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const translations = formData.translations
        .filter(t => t.languageCode.trim() && t.name.trim())
        .map(t => ({
          languageCode: t.languageCode.trim(),
          name: t.name.trim()
        }));

      const data = new FormData();
      data.append('name', formData.name.trim());
      data.append('cityCode', formData.cityCode);
      data.append('isEnabled', formData.isEnabled);

      // Pass new image uploads as pictures (only once)
      formData.Pictures.forEach((file) => {
        data.append('pictures', file);
      });

      // Pass kept existing image URLs as picturesKeep
      const keepUrls = (formData.existingBannerImages || [])
        .map((u) => String(u).trim())
        .filter(Boolean);
      keepUrls.forEach((url) => {
        data.append('picturesKeep', url);
      });

      // Pass removed image URLs as picturesRemove
      const picturesRemove = formData.picturesRemove || [];
      picturesRemove.forEach((url) => {
        data.append('picturesRemove', url);
      });

      translations.forEach((t, index) => {
        data.append(`Translations[${index}].LanguageCode`, t.languageCode);
        data.append(`Translations[${index}].Name`, t.name);
      });

      if (selectedCity) {
        data.append('id', selectedCity.id);
        await citiesAPI.updateCity(selectedCity.id, data);
        showToast(t('cities.toastUpdated'), 'success');
      } else {
        await citiesAPI.createCity(data);
        showToast(t('cities.toastCreated'), 'success');
      }
      fetchCities();
      handleClose();
    } catch (error) {
      console.error('Error saving city:', error);
      showToast(error?.response?.data?.errorMessage || t('cities.toastSaveError'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (browserGlobal.confirm(t('cities.confirmDelete'))) {
      setIsLoading(true);
      try {
        await citiesAPI.deleteCity(id);
        showToast(t('cities.toastDeleted'), 'success');
        fetchCities();
      } catch (error) {
        console.error('Error deleting city:', error);
        showToast(error?.response?.data?.errorMessage || t('cities.toastDeleteError'), 'error');
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Box p={1}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mb={2}
        flexDirection={{ xs: 'column', sm: 'row' }}
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="h4" color="primary">
          {t('cities.pageTitle')}
        </Typography>
        {isSuperAdmin && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {t('cities.addNew')}
          </Button>
        )}
      </Box>
      <Box sx={{ mb: 3, p: 2, bgcolor: '#fff', borderRadius: 1, boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label={t('common.searchByName')}
              value={filters.name}
              onChange={(e) => setFilters(prev => ({ ...prev, name: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label={t('common.searchByCode')}
              value={filters.cityCode}
              onChange={(e) => setFilters(prev => ({ ...prev, cityCode: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('common.isEnabled')}</InputLabel>
              <Select
                value={filters.status}
                label={t('common.isEnabled')}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="">{t('common.all') || 'All'}</MenuItem>
                <MenuItem value="true">{t('common.yes')}</MenuItem>
                <MenuItem value="false">{t('common.no')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md="auto"
            sx={{ display: 'flex', alignItems: 'center', ml: { md: 'auto' } }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterAltOff />}
              onClick={handleClearFilters}
              sx={{ ...clearFilterButtonSx, width: { xs: '100%', md: 'auto' } }}
            >
              {t('common.clear')}
            </Button>
          </Grid>
        </Grid>
      </Box>
      {isLoading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress sx={{ color: '#0F3D3E' }} />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            overflowX: 'auto',
            boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)',
            borderRadius: 2,
            '& .MuiTableCell-head': {
              backgroundColor: '#ebf4f4',
              color: '#0F3D3E',
              fontWeight: 600,
              padding: '16px'
            },
            '& .MuiTableCell-body': {
              padding: '16px'
            }
          }}
        >
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('cities.colImage')}</TableCell>
                <TableCell>{t('common.name')}</TableCell>
                <TableCell>{t('cities.colCityCode')}</TableCell>
                <TableCell>{t('common.translations')}</TableCell>
                <TableCell>{t('cities.colIsEnabled')}</TableCell>
                <TableCell>{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cities
                .filter(city => {
                  const matchesName = (city.name || '').toLowerCase().includes((filters.name || '').toLowerCase());
                  const matchesCode = (city.cityCode || '').toLowerCase().includes((filters.cityCode || '').toLowerCase());
                  const matchesStatus = filters.status === 'true' ? city.isEnabled : filters.status === 'false' ? !city.isEnabled : true;
                  return matchesName && matchesCode && matchesStatus;
                })
                .map((city) => (
                  <TableRow
                    key={city.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#ebf4f4',
                      }
                    }}
                  >
                    <TableCell>
                      <Box
                        sx={{
                          width: 120,
                          height: 70,
                          borderRadius: 1,
                          overflow: 'hidden',
                          bgcolor: '#ebf4f4',
                          boxShadow: '0 2px 4px rgba(124, 58, 237, 0.1)',
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'scale(1.05)'
                          }
                        }}
                      >
                        <img
                          src={(() => {
                            let imgUrl = null;
                            if (city.pictures && Array.isArray(city.pictures) && city.pictures.length > 0) {
                              const firstPic = city.pictures[0];
                              imgUrl = typeof firstPic === 'string' ? firstPic : (firstPic.imageUrl || firstPic.url);
                            }
                            if (!imgUrl && city.imageUrl) {
                              imgUrl = city.imageUrl.split(',')[0];
                            }
                            return imgUrl || defaultCityImage;
                          })()}
                          alt={city.name}
                          onError={(e) => {
                            e.target.src = defaultCityImage;
                            e.target.onerror = null;
                          }}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block'
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>{city.name}</TableCell>
                    <TableCell>{city.cityCode}</TableCell>
                    <TableCell>
                      {city.translations?.length
                        ? city.translations.map((t) => `${t.languageCode}: ${t.name}`).join(', ')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={city.isEnabled ? t('common.yes') : t('common.no')}
                        color={city.isEnabled ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleOpen(city)}
                        sx={{ color: '#0F3D3E' }}
                      >
                        <Edit />
                      </IconButton>
                      {isSuperAdmin && (
                        <IconButton
                          onClick={() => handleDelete(city.id)}
                          sx={{ color: '#ef4444' }}
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" fullScreen={isMobileView}>
        <DialogTitle>{selectedCity ? t('cities.dialogEdit') : t('cities.dialogAdd')}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            name="name"
            label={t('cities.fieldCityName')}
            fullWidth
            value={formData.name}
            onChange={handleChange}
          />
          <TextField
            margin="dense"
            name="cityCode"
            label={t('cities.fieldCityCode')}
            fullWidth
            value={formData.cityCode}
            onChange={handleChange}
          />
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mt: 1, mb: 1 }}>
              Banner Image
            </Typography>

            <Box sx={{
              p: 2,
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              bgcolor: '#f9fafb',
              minHeight: '100px',
              mb: 2
            }}>
              <Grid container spacing={2}>
                {/* Existing Images */}
                {formData.existingBannerImages.map((url, idx) => (
                  <Grid item key={`existing-${idx}`}>
                    <Box sx={{
                      position: 'relative',
                      width: 80,
                      height: 80,
                      '&:hover .delete-btn': { opacity: 1 }
                    }}>
                      <img
                        src={url}
                        alt="Existing"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '12px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      />
                      <IconButton
                        size="small"
                        className="delete-btn"
                        onClick={() => handleRemoveImage(idx, true)}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          bgcolor: '#fff',
                          opacity: 0.8,
                          transition: 'all 0.2s',
                          '&:hover': { bgcolor: '#ef4444', color: '#fff', opacity: 1 },
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          p: 0.5
                        }}
                      >
                        <Close sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}

                {/* New Selected Images */}
                {formData.Pictures.map((file, idx) => (
                  <Grid item key={`new-${idx}`}>
                    <Box sx={{
                      position: 'relative',
                      width: 80,
                      height: 80,
                      '&:hover .delete-btn': { opacity: 1 }
                    }}>
                      <img
                        src={URL.createObjectURL(file)}
                        alt="New"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '12px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      />
                      <IconButton
                        size="small"
                        className="delete-btn"
                        onClick={() => handleRemoveImage(idx)}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          bgcolor: '#fff',
                          opacity: 0.8,
                          transition: 'all 0.2s',
                          '&:hover': { bgcolor: '#ef4444', color: '#fff', opacity: 1 },
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          p: 0.5
                        }}
                      >
                        <Close sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}

                {/* Add Image Button */}
                <Grid item>
                  <Button
                    component="label"
                    sx={{
                      width: 80,
                      height: 80,
                      border: '2px dashed #d1d5db',
                      borderRadius: '12px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#6b7280',
                      textTransform: 'none',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: '#0F3D3E',
                        color: '#0F3D3E',
                        bgcolor: 'rgba(124, 58, 237, 0.04)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <AddPhotoAlternate sx={{ fontSize: 28 }} />
                    <Typography variant="caption" sx={{ mt: 1, fontWeight: 500 }}>{t('common.add') || 'Add'}</Typography>
                    <input
                      type="file"
                      hidden
                      multiple
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Box mt={3}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t('common.translationsRequired')}
            </Typography>
            {formData.translations.map((tr, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start',
                  mb: 2,
                }}
              >
                <FormControl required size="small" sx={{ flex: 1 }}>
                  <InputLabel id={`lang-select-${index}`}>{t('common.language')}</InputLabel>
                  <Select
                    labelId={`lang-select-${index}`}
                    label={t('common.language')}
                    value={tr.languageCode || ''}
                    onChange={(e) => handleTranslationChange(index, 'languageCode', e.target.value)}
                  >
                    {languages.map((lang) => (
                      <MenuItem key={lang.id || lang.languageCode} value={lang.languageCode || ''}>
                        {lang.name || lang.languageCode || ''}
                      </MenuItem>
                    ))}
                    {tr.languageCode && !languages.some((l) => (l.languageCode || '') === tr.languageCode) && (
                      <MenuItem value={tr.languageCode}>{tr.languageCode}</MenuItem>
                    )}
                  </Select>
                </FormControl>
                <TextField
                  required
                  size="small"
                  label={t('common.name')}
                  value={tr.name}
                  onChange={(e) => handleTranslationChange(index, 'name', e.target.value)}
                  placeholder={t('common.translatedNamePh')}
                  sx={{ flex: 1 }}
                />
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removeTranslation(index)}
                  disabled={formData.translations.length <= 1}
                >
                  <Delete />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<Add />}
              onClick={addTranslation}
              sx={{ mt: 1, color: '#0F3D3E' }}
            >
              {t('common.addTranslation')}
            </Button>
          </Box>

          <Box mt={2}>
            <FormControlLabel
              control={
                <Switch
                  name="isEnabled"
                  checked={formData.isEnabled}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      isEnabled: e.target.checked
                    }));
                  }}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#0F3D3E',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#0F3D3E',
                    },
                  }}
                />
              }
              label={t('common.active')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : null}
          >
            {selectedCity ? t('common.update') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastOpen(false)} severity={toast.type} sx={{ width: '100%' }}>
          {toast.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CityList;
